import { Button, Input, Upload, message, Form, Select } from "antd";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { updateAccountDetails } from "Sdk/Actions";
import {
  getUserDetailsFromReducer,
  getUserNameFromReducer,
} from "Store/reducers/Auth/AuthSlice";
import { uploadSeoImage } from "Utility/UploadFile";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/free-solid-svg-icons";
import CustomImage from "@/components/CustomImage";

const AdvanceSEO = () => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    keywords: "",
    author: "",
    ogTitle: "",
    ogDescription: "",
    ogImage: "",
    ogURL: "",
    twitterCard: "",
    twitterTitle: "",
    twitterDescription: "",
    twitterImage: "",
  });
  const [imagePreviews, setImagePreviews] = useState({
    ogImage: "",
    twitterImage: "",
  });
  const [saving, setSaving] = useState(false);

  const userName = useSelector(getUserNameFromReducer);
  const userDetails = useSelector(getUserDetailsFromReducer);
  const [form] = Form.useForm();

  useEffect(() => {
    let seo = userDetails?.advancedseo;
    if (seo) {
      try {
        let data = JSON.parse(seo);
        // Convert keywords string to array for Select
        if (data.keywords && typeof data.keywords === "string") {
          data.keywords = data.keywords
            .split(",")
            .map((k) => k.trim())
            .filter(Boolean);
        }
        setFormData((prev) => ({ ...prev, ...data }));
        form.setFieldsValue({ ...formData, ...data });
      } catch (error) {
        console.log(error);
      }
    }
    // eslint-disable-next-line
  }, [userDetails]);

  const handleUpdateSeoData = async () => {
    setSaving(true);
    try {
      if (formData) {
        // Convert keywords array to string for saving
        let saveData = { ...formData };
        if (Array.isArray(saveData.keywords)) {
          saveData.keywords = saveData.keywords.join(",");
        }

        console.log(saveData, "Saves seo data");
        let seoStringified = JSON.stringify(saveData);
        const userData = {
          username: userName,
          advancedseo: seoStringified,
        };
        await updateAccountDetails(userData);
        message.success("SEO data saved successfully!");
      } else {
        console.log("formData not found");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 px-2 py-6 md:px-8 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-xl lg:text-2xl font-bold text-center mb-4">
          AI SEO Settings
        </h1>
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-4 lg:gap-6">
          {/* Tips/Example - on small screens, show on top; on large, right side */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <div className="bg-white rounded-2xl shadow-xl p-4 lg:p-6 flex flex-col items-center justify-center min-h-[300px]">
              <div className="text-gray-500 text-center">
                <div className="font-semibold mb-2">SEO Tips</div>
                <ul className="list-disc pl-4 text-left text-sm mb-4">
                  <li>Use clear, descriptive titles and descriptions.</li>
                  <li>Include relevant keywords for your business.</li>
                  <li>Upload a high-quality logo or image for sharing.</li>
                  <li>
                    Fill out Open Graph and Twitter fields for better social
                    sharing.
                  </li>
                </ul>
                <div className="mb-2 font-semibold">
                  Example for a Dental Clinic Website:
                </div>
                <div className="mb-2 text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-900">
                  <ul className="list-disc pl-4 text-left">
                    <li>
                      <b>Meta Title:</b> Bright Smiles Dental Clinic | Family
                      Dentist in New York
                    </li>
                    <li>
                      <b>Meta Description:</b> Bright Smiles Dental Clinic
                      offers gentle, affordable dental care for all ages in New
                      York. Book your appointment today!
                    </li>
                    <li>
                      <b>Keywords:</b> dentist, dental clinic, teeth cleaning,
                      New York, family dentistry
                    </li>
                    <li>
                      <b>Author:</b> Dr. Jane Doe
                    </li>
                    <li>
                      <b>Open Graph Title:</b> Bright Smiles Dental Clinic
                    </li>
                    <li>
                      <b>Open Graph Description:</b> Your trusted family dentist
                      in New York. Gentle care, modern technology.
                    </li>
                    <li>
                      <b>Open Graph URL:</b> https://brightsmilesny.com
                    </li>
                    <li>
                      <b>Twitter Card:</b> summary_large_image
                    </li>
                    <li>
                      <b>Twitter Title:</b> Bright Smiles Dental Clinic
                    </li>
                    <li>
                      <b>Twitter Description:</b> Gentle, affordable dental care
                      for your family in New York.
                    </li>
                    <li>
                      <b>Images:</b> (Upload your clinic logo or a team photo)
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          {/* SEO Form - 70% width on large screens */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="bg-white rounded-2xl shadow-xl p-4 lg:p-6">
              <h2 className="text-lg font-bold mb-4">SEO Information</h2>
              <Form
                form={form}
                layout="vertical"
                onValuesChange={(changed, all) => setFormData(all)}
                onFinish={handleUpdateSeoData}
              >
                {/* Title, Description, Keywords, Author */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Form.Item
                    label={<span className="text-gray-500">Meta Title</span>}
                    name="title"
                    className="mb-2"
                  >
                    <Input
                      className="input-border"
                      placeholder="e.g. Bright Smiles Dental Clinic | Family Dentist in New York"
                    />
                  </Form.Item>
                  <Form.Item
                    label={
                      <span className="text-gray-500">Meta Description</span>
                    }
                    name="description"
                    className="mb-2"
                  >
                    <Input
                      className="input-border"
                      placeholder="e.g. Gentle, affordable dental care for all ages in New York. Book your appointment today!"
                    />
                  </Form.Item>
                  <Form.Item
                    label={<span className="text-gray-500">Keywords</span>}
                    name="keywords"
                    className="mb-2"
                  >
                    <Select
                      mode="tags"
                      style={{ width: "100%" }}
                      placeholder="Type a keyword and press Enter"
                      tokenSeparators={[","]}
                      open={false}
                      suffixIcon={null}
                    />
                  </Form.Item>
                  <Form.Item
                    label={<span className="text-gray-500">Author</span>}
                    name="author"
                    className="mb-2"
                  >
                    <Input
                      className="input-border"
                      placeholder="e.g. Dr. Jane Doe"
                    />
                  </Form.Item>
                </div>
                <div className="my-4">
                  <hr className="border-gray-200" />
                </div>
                {/* Open Graph Section */}
                <div className="pt-2">
                  <h3 className="font-semibold mb-2">
                    Open Graph (Facebook/LinkedIn)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Form.Item
                      label={<span className="text-gray-500">OG Title</span>}
                      name="ogTitle"
                    >
                      <Input
                        className="input-border"
                        placeholder="e.g. Bright Smiles Dental Clinic"
                      />
                    </Form.Item>
                    <Form.Item
                      label={
                        <span className="text-gray-500">OG Description</span>
                      }
                      name="ogDescription"
                    >
                      <Input
                        className="input-border"
                        placeholder="e.g. Your trusted family dentist in New York. Gentle care, modern technology."
                      />
                    </Form.Item>
                    <Form.Item
                      label={<span className="text-gray-500">OG URL</span>}
                      name="ogURL"
                    >
                      <Input
                        className="input-border"
                        placeholder="e.g. https://brightsmilesny.com"
                      />
                    </Form.Item>
                    <Form.Item
                      label={<span className="text-gray-500">OG Image</span>}
                    >
                      <Upload.Dragger
                        name="file"
                        multiple={false}
                        showUploadList={false}
                        accept="image/*"
                        className="border-radius"
                        beforeUpload={async (file) => {
                          const getBase64 = (file) =>
                            new Promise((resolve, reject) => {
                              const reader = new FileReader();
                              reader.readAsDataURL(file);
                              reader.onload = () => resolve(reader.result);
                              reader.onerror = (error) => reject(error);
                            });
                          const base64 = await getBase64(file);
                          setImagePreviews((prev) => ({
                            ...prev,
                            ogImage: base64,
                          }));
                          try {
                            const res = await uploadSeoImage(file, userName);
                            setFormData((prev) => ({
                              ...prev,
                              ogImage: res.key,
                            }));
                            console.log(
                              "OG Image S3 Key after upload:",
                              res.key
                            );
                          } catch (err) {
                            message.error("Failed to upload image");
                          }
                          return false;
                        }}
                      >
                        {imagePreviews.ogImage ? (
                          <img
                            src={imagePreviews.ogImage}
                            alt="SEO Preview"
                            className="mt-2 rounded-md border w-32 h-20 object-cover"
                          />
                        ) : formData.ogImage ? (
                          <CustomImage
                            S3Key={
                              Array.isArray(formData.ogImage)
                                ? formData.ogImage[0]
                                : formData.ogImage
                            }
                            alt="SEO Preview"
                            className="mt-2 rounded-md border w-32 h-20 object-cover"
                          />
                        ) : (
                          <p className="text-md w-full h-full flex flex-col items-center justify-center">
                            <FontAwesomeIcon icon={faUpload} />
                            Upload your logo or a team photo
                          </p>
                        )}
                      </Upload.Dragger>
                    </Form.Item>
                  </div>
                </div>
                <div className="my-4">
                  <hr className="border-gray-200" />
                </div>
                {/* Twitter Section */}
                <div className="pt-2">
                  <h3 className="font-semibold mb-2">Twitter</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Form.Item
                      label={
                        <span className="text-gray-500">Twitter Card</span>
                      }
                      name="twitterCard"
                    >
                      <Input
                        className="input-border"
                        placeholder="e.g. summary_large_image"
                      />
                    </Form.Item>
                    <Form.Item
                      label={
                        <span className="text-gray-500">Twitter Title</span>
                      }
                      name="twitterTitle"
                    >
                      <Input
                        className="input-border"
                        placeholder="e.g. Bright Smiles Dental Clinic"
                      />
                    </Form.Item>
                    <Form.Item
                      label={
                        <span className="text-gray-500">
                          Twitter Description
                        </span>
                      }
                      name="twitterDescription"
                    >
                      <Input
                        className="input-border"
                        placeholder="e.g. Gentle, affordable dental care for your family in New York."
                      />
                    </Form.Item>
                    <Form.Item
                      label={
                        <span className="text-gray-500">Twitter Image</span>
                      }
                    >
                      <Upload.Dragger
                        name="file"
                        multiple={false}
                        showUploadList={false}
                        accept="image/*"
                        className="border-radius"
                        beforeUpload={async (file) => {
                          const getBase64 = (file) =>
                            new Promise((resolve, reject) => {
                              const reader = new FileReader();
                              reader.readAsDataURL(file);
                              reader.onload = () => resolve(reader.result);
                              reader.onerror = (error) => reject(error);
                            });
                          const base64 = await getBase64(file);
                          setImagePreviews((prev) => ({
                            ...prev,
                            twitterImage: base64,
                          }));
                          try {
                            const res = await uploadSeoImage(file, userName);
                            setFormData((prev) => ({
                              ...prev,
                              twitterImage: res.key,
                            }));
                            console.log(
                              "Twitter Image S3 Key after upload:",
                              res.key
                            );
                          } catch (err) {
                            message.error("Failed to upload image");
                          }
                          return false;
                        }}
                      >
                        {imagePreviews.twitterImage ? (
                          <img
                            src={imagePreviews.twitterImage}
                            alt="SEO Preview"
                            className="mt-2 rounded-md border w-32 h-20 object-cover"
                          />
                        ) : formData.twitterImage ? (
                          <CustomImage
                            S3Key={
                              Array.isArray(formData.twitterImage)
                                ? formData.twitterImage[0]
                                : formData.twitterImage
                            }
                            alt="SEO Preview"
                            className="mt-2 rounded-md border w-32 h-20 object-cover"
                          />
                        ) : (
                          <p className="text-md w-full h-full flex flex-col items-center justify-center">
                            <FontAwesomeIcon icon={faUpload} />
                            Upload your logo or a team photo
                          </p>
                        )}
                      </Upload.Dragger>
                    </Form.Item>
                  </div>
                </div>
                <div className="flex justify-end mt-6 space-x-2 border-t pt-6">
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    className="rounded-xl px-6 mt-3 font-bold w-full"
                    loading={saving}
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvanceSEO;
